/**
 * Role — the deployment's role catalog: exactly one built-in doc, `owner`,
 * carrying the whole permission catalog, plus any custom role a
 * `tickets.roles.manage` holder creates — including the seeded, non-built-in
 * `checkin` example role the door-phone provisioning path depends on.
 *
 * Path: `workspaces/{orgId}/roles/{roleId}` — `owner` alone is Admin-SDK
 * provisioned and structurally immutable; every other role, seeded or not, is
 * an ordinary doc created/edited/deleted under `tickets.roles.manage`. The
 * catalog lives on the ORG even for an event-scoped grant — a role is
 * never replicated onto an event.
 *
 * Fields justified:
 *   name         — display string for the role admin surface's row and the
 *                  permission picker's header.
 *   permissions  — the tokens this role grants; validated against the closed
 *                  catalog at this seam, so an unknown token cannot be stored.
 *   isBuiltIn    — the discriminant: `true` on the one seeded `owner` doc,
 *                  `false` on every other role, seeded (`checkin`) or
 *                  custom. Write rules gate on `isBuiltIn.eq(false)`, which
 *                  is what makes the `owner` doc structurally immutable. Its
 *                  earlier exclusion ("a flag with one value distinguishes
 *                  nothing") held only while no custom-role path existed; the
 *                  field returns now that one does.
 *
 * Excluded:
 *   id               — the doc id is the role id; no rule reads it as a field
 *                      (the read gate is bare membership, not `ownRole`).
 *   workspaceOwnerId — no owner-bootstrap write path; provisioning is scripted.
 *   createdAt        — no reader consumes a role's age.
 */
import { entity } from "@mhmo91/ican";
import { createRoleEntity, createRoleSchema } from "@momo/membership";
import type { z } from "zod";
import { PermissionSchema } from "./permissions.ts";
import type { Actor } from "./workspace.entity.ts";

export const RoleSchema = createRoleSchema({ permission: PermissionSchema });
export type Role = z.infer<typeof RoleSchema>;

// read + list: any member of the org, second factor verified — a deliberate
// divergence from momo-starter's manage-gated role list: every
// `members.view` holder's member list is role-grouped, so gating role read
// on `roles.manage` would blank the member list for non-owners; a custom
// role's permission set becomes visible to every member as the tradeoff.
// The role docs are a small, non-confidential set — revealing what
// "checkin" carries to another holder discloses nothing — so bare
// membership is the gate rather than `ownRole`. That also sidesteps
// `ownRole`'s kernel dead zone (it resolves through `data.id`, which the
// runtime cannot see) and keeps the rule list-safe: `member()` resolves
// through grant-doc `get()`s and `me.mfa` through the ID token — neither
// touches `resource.data`, which is the actual list-safety test, so the
// emitter produces `allow read` AND `allow list`. List is what the role
// admin surface enumerates.
//
// create/update/delete: server-only. The Firestore rule this replaces gated
// on `data.isBuiltIn.eq(false)` + `hasPermission('tickets.roles.manage')`
// alone — NO subset-rule guard: a `roles.manage` holder could mint a role
// carrying every catalog token, including `roles.manage` and
// `members.invite` themselves, then self-assign it via Grant's own write
// door. `createRole`/`editRole`/`deleteRole`
// (firebase/src/members/) run the same containment spine
// `createInvite.api.ts` already runs — the proposed (create) or patched
// (edit) permission set must be ⊆ the caller's own role's permissions — and
// are the sole entry points. They also carry forward the `isBuiltIn`
// immutability guard this rule used to enforce declaratively: `editRole`/
// `deleteRole` reject a target role whose `isBuiltIn` is `true`, and
// `createRole`'s own wire contract has no `isBuiltIn` field at all — the
// handler always stamps `false` — so the seeded `owner` doc can never be
// reached, reshaped, or deleted through either door.
export const Role = createRoleEntity(entity<Actor>(), {
	schema: RoleSchema,
	hierarchicalGrants: true,
	namespace: "tickets.roles",
	path: "workspaces/{orgId}/roles/{roleId}",
	handlers: { create: "createRole", update: "editRole", delete: "deleteRole" },
	readPolicy: ({ can, member, and }) => [
		can("read").when((_data, me) =>
			and(member("orgId", { envelope: true }), me.mfa.eq(true)),
		),
		can("list").when((_data, me) =>
			and(member("orgId", { envelope: true }), me.mfa.eq(true)),
		),
	],
});
